import type { ReactNode } from 'react';

import { useIsSerialNumberTrackingAvailable } from '../feature-gates/use-is-serial-number-tracking-available';
import { SerialNumberProvider, useSerialNumberContext } from './serial-number-context';

type EnsureSerialNumberProviderProps = {
  children: ReactNode;
  fulfillmentPlanId?: string;
};

// Mounts a SerialNumberProvider only where an ancestor hasn't already mounted one for the same
// plan. Print entry points are shared: order details and scan-to-verify provide serials for the
// plan and their rows are what call refresh() after a save, while the orders drawer, the
// shipment-details modal, create-label and the standalone scan-to-print page provide nothing at
// all. Nesting a second provider under an existing one would shadow it with a copy that only ever
// loads on mount, so the print guard would keep reporting a serial as missing after it was
// captured — hence the ancestor check instead of an unconditional wrapper.
export const EnsureSerialNumberProvider = ({ children, fulfillmentPlanId }: EnsureSerialNumberProviderProps) => {
  const { fulfillmentPlanId: ancestorFulfillmentPlanId } = useSerialNumberContext();
  const { isAvailable: isSerialNumberTrackingAvailable } = useIsSerialNumberTrackingAvailable();

  const hasAncestorProviderForPlan = ancestorFulfillmentPlanId != null && ancestorFulfillmentPlanId === Number(fulfillmentPlanId);

  if (hasAncestorProviderForPlan) {
    return <>{children}</>;
  }

  return (
    <SerialNumberProvider fulfillmentPlanId={fulfillmentPlanId} isEnabled={isSerialNumberTrackingAvailable}>
      {children}
    </SerialNumberProvider>
  );
};
